<template>
  <v-navigation-drawer
    :mini-variant="miniVariant"
    :mini-variant-width="72"
    :clipped="clipped"
    v-model="drawer"
    fixed
    app
    class="drawer-left"
    :class="{ 'drawer-left--rail': rail }"
    width="280"
  >
    <!-- ============ Store header (prepend) — ported 1:1 from possmart-vue SideNav.vue ============ -->
    <template v-slot:prepend>
      <div class="ps-store-header" :class="{ 'ps-store-header--rail': rail }">
        <span class="ps-store-logo">
          <img
            v-if="storeLogo"
            :src="storeLogo"
            alt=""
            class="ps-store-logo__img"
          />
          <v-icon v-else class="ps-store-logo__icon">fal fa-store</v-icon>
        </span>
        <div
          v-if="!rail"
          class="ps-store-meta"
          :class="{ 'ps-store-meta--switchable': canSwitchBranch }"
          :role="canSwitchBranch ? 'button' : undefined"
          :tabindex="canSwitchBranch ? 0 : undefined"
          @click="openBranchSwitcher"
          @keydown.enter="openBranchSwitcher"
          @keydown.space.prevent="openBranchSwitcher"
        >
          <p class="ps-store-name">{{ storeName }}</p>
          <p v-if="branchName" class="ps-store-branch">
            <v-icon x-small class="mr-1">fal fa-building</v-icon>
            {{ branchName }}
            <v-icon v-if="canSwitchBranch" x-small class="ml-1"
              >fal fa-chevron-down</v-icon
            >
          </p>
          <p v-else class="ps-store-branch">
            {{ $t("system") }}
          </p>
        </div>
      </div>
    </template>

    <!-- ============ Nav — ported from SideNav's expanded + collapsed "rail" templates ============ -->
    <v-list v-if="menuSections.length" class="ps-menu">
      <!-- collapsed icon rail: one icon per top-level menu; tooltip shows the name -->
      <template v-if="rail">
        <div v-for="section in menuSections" :key="section.key">
          <template v-for="(item, i) in section.items">
            <!-- group with children: expand the sidebar on click -->
            <v-tooltip
              v-if="item.menu && item.menu.length"
              :key="`rail-g-${section.key}-${item.code || item.id || i}`"
              right
              content-class="ps-rail-tooltip"
            >
              <template v-slot:activator="{ on }">
                <v-list-item
                  link
                  class="ps-rail-item"
                  v-on="on"
                  :class="{ 'ps-rail-active': railActive(item) }"
                  @click="expandFromRail(item)"
                >
                  <span class="ps-menu-icon">
                    <app-img
                      v-if="isUploadedIcon(item)"
                      :img="item.icon"
                      width="16"
                      height="16"
                      contain
                    />
                    <i
                      v-else-if="isLineIcon(item)"
                      :class="item.icon"
                      class="ps-sidebar-lni"
                    ></i>
                    <v-icon v-else-if="item.icon" :size="16">{{
                      item.icon
                    }}</v-icon>
                  </span>
                </v-list-item>
              </template>
              <span>{{ getMenuLabel(item) }}</span>
            </v-tooltip>
            <!-- single menu item: navigate directly -->
            <v-tooltip
              v-else
              :key="`rail-i-${section.key}-${item.code || item.id || i}`"
              right
              content-class="ps-rail-tooltip"
            >
              <template v-slot:activator="{ on }">
                <v-list-item
                  :to="item.to"
                  link
                  class="ps-rail-item"
                  v-on="on"
                  :class="{ 'ps-rail-active': railActive(item) }"
                >
                  <span class="ps-menu-icon">
                    <app-img
                      v-if="isUploadedIcon(item)"
                      :img="item.icon"
                      width="16"
                      height="16"
                      contain
                    />
                    <i
                      v-else-if="isLineIcon(item)"
                      :class="item.icon"
                      class="ps-sidebar-lni"
                    ></i>
                    <v-icon v-else-if="item.icon" :size="16">{{
                      item.icon
                    }}</v-icon>
                  </span>
                </v-list-item>
              </template>
              <span>{{ getMenuLabel(item) }}</span>
            </v-tooltip>
          </template>
        </div>
      </template>

      <!-- expanded menu -->
      <template v-else>
        <div v-for="section in menuSections" :key="section.key">
          <div
            v-if="section.header"
            class="ps-menu-section-label px-4 pt-5 pb-1 text-uppercase"
          >
            {{ getMenuLabel(section.header) }}
          </div>
          <template v-for="(item, i) in section.items">
            <!-- collapsible group header stays neutral — only the active sub-item highlights -->
            <v-list-group
              v-if="item.menu && item.menu.length"
              :key="groupKey(item)"
              sub-group
              no-action
              :value="isGroupOpen(item)"
              @input="(val) => onGroupChange(item, val)"
            >
              <template v-slot:prependIcon>
                <span class="ps-menu-icon">
                  <app-img
                    v-if="isUploadedIcon(item)"
                    :img="item.icon"
                    width="16"
                    height="16"
                    contain
                  />
                  <i
                    v-else-if="isLineIcon(item)"
                    :class="item.icon"
                    class="ps-sidebar-lni"
                  ></i>
                  <v-icon v-else-if="item.icon" :size="16">{{
                    item.icon
                  }}</v-icon>
                </span>
              </template>
              <template v-slot:appendIcon>
                <v-icon x-small class="ps-group-chevron"
                  >fal fa-chevron-down</v-icon
                >
              </template>
              <template v-slot:activator>
                <v-list-item-title>{{ getMenuLabel(item) }}</v-list-item-title>
              </template>
              <v-list-item
                v-for="(data, key) in item.menu"
                :key="key"
                :to="data.to"
                link
              >
                <v-list-item-title>{{ getMenuLabel(data) }}</v-list-item-title>
              </v-list-item>
            </v-list-group>

            <v-list-item
              v-else
              :key="`${section.key}-${item.code || item.id || i}`"
              :to="item.to"
              link
            >
              <v-list-item-icon>
                <span class="ps-menu-icon">
                  <app-img
                    v-if="isUploadedIcon(item)"
                    :img="item.icon"
                    width="16"
                    height="16"
                    contain
                  />
                  <i
                    v-else-if="isLineIcon(item)"
                    :class="item.icon"
                    class="ps-sidebar-lni"
                  ></i>
                  <v-icon v-else-if="item.icon" :size="16">{{
                    item.icon
                  }}</v-icon>
                </span>
              </v-list-item-icon>
              <v-list-item-title>{{ getMenuLabel(item) }}</v-list-item-title>
            </v-list-item>
          </template>
        </div>
      </template>
    </v-list>

    <!-- ============ User footer (append) — ported from SideNav's user block ============ -->
    <template v-slot:append>
      <div class="ps-user-wrap" :class="{ 'ps-user-wrap--rail': rail }">
        <div class="ps-user" :class="{ 'ps-user--rail': rail }">
          <span class="ps-user-avatar">
            <img
              v-if="userAvatar"
              :src="userAvatar"
              alt=""
              class="ps-user-avatar__img"
            />
            <span v-else class="ps-user-avatar__initials">{{ initials }}</span>
          </span>
          <template v-if="!rail">
            <div class="ps-user-meta">
              <p class="ps-user-name">{{ displayName }}</p>
              <p class="ps-user-role">{{ userRole(Number(user.role)) }}</p>
            </div>
            <button
              class="ps-logout-btn"
              :title="$t('logout')"
              :aria-label="$t('logout')"
              @click="logoutMethod"
            >
              <v-icon small>fal fa-sign-out-alt</v-icon>
            </button>
          </template>
          <v-tooltip v-else right content-class="ps-rail-tooltip">
            <template v-slot:activator="{ on }">
              <button
                class="ps-logout-btn ps-logout-btn--rail"
                v-on="on"
                :aria-label="$t('logout')"
                @click="logoutMethod"
              >
                <v-icon small>fal fa-sign-out-alt</v-icon>
              </button>
            </template>
            <span>{{ $t("logout") }}</span>
          </v-tooltip>
        </div>
      </div>
    </template>

    <app-custom-dialog
      v-model="dialog"
      :title="form.title"
      @submit="joinSubmit"
    >
      <div slot="activator">
        <v-row>
          <v-col cols="12">
            <v-alert type="info" outlined>
              {{ $t("join_e_commerce_description") }}
            </v-alert>
          </v-col>
          <v-col cols="6" v-for="(item, i) in packages" :key="i">
            <v-card class="pa-2" outlined height="250px">
              <v-card-title class="text-center text-capitalize">{{
                item.name
              }}</v-card-title>
              <v-card-text class="text-center">
                <span class="text-h6 font-weight-bold">{{
                  item.price | NumberFormat
                }}</span>
                {{ $t("lak") }}/
                <span>
                  {{ $t("month") }}
                </span>

                <p>{{ $t(item.desc) }}</p>
              </v-card-text>
              <v-card-actions class="text-center">
                <v-btn
                  block
                  depressed
                  class="text-capitalize"
                  :color="form.package === item.value ? 'primary' : 'grey'"
                  @click="form.package = item.value"
                >
                  <v-icon small left>{{ item.icon }}</v-icon>
                  {{ $t(item.btn) }}</v-btn
                >
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </div>
    </app-custom-dialog>
    <app-custom-dialog
      v-model="logoutDialog"
      title="confirm_logout"
      width="420px"
      cancel-text="cancel"
      submit-text="logout"
      submit-color="error"
      @submit="confirmLogout"
    >
      <div slot="activator" class="logout-confirmation">
        {{ $t("want_to_logout") }}
      </div>
    </app-custom-dialog>
    <app-custom-dialog
      v-model="branchDialog"
      title="change_branch"
      width="600px"
      submit-text="change_branch"
      :submit-disabled="!selectedBranchId || branchLoading || branchSwitching"
      :submit-loading="branchSwitching"
      @submit="confirmBranchSwitch"
    >
      <div slot="activator">
        <v-progress-linear v-if="branchLoading" indeterminate color="primary" />
        <v-radio-group
          v-else-if="availableBranches.length"
          v-model="selectedBranchId"
          class="branch-radio-group ma-0"
          hide-details
        >
          <v-list class="pa-0">
            <v-list-item
              v-for="branch in availableBranches"
              :key="branch.id"
              :class="{
                'branch-option--active':
                  String(selectedBranchId) === String(branch.id),
              }"
              :disabled="branch.is_expired"
              @click="!branch.is_expired && (selectedBranchId = branch.id)"
            >
              <v-list-item-action>
                <v-radio
                  :value="branch.id"
                  :disabled="branch.is_expired"
                  color="primary"
                />
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>{{ branch.name }}</v-list-item-title>
                <v-list-item-subtitle v-if="branch.address">
                  {{ branch.address }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <branch-status-chip :branch="branch" />
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-radio-group>
        <div v-else class="text-center py-4 grey--text">
          {{ $t("no_data") }}
        </div>
      </div>
    </app-custom-dialog>
  </v-navigation-drawer>
</template>

<script>
import { mapGetters } from "vuex";
import { myMixin } from "@/mixin/main";
import { toast, confirm } from "@/controllers/alert";
import { setUser } from "@/controllers/auth";
import BranchStatusChip from "@/components/BranchStatusChip.vue";

const getDrawer = () => {
  const drawer = localStorage.getItem("drawer");
  if (drawer) {
    return drawer === "true" ? true : false;
  } else {
    return true;
  }
};

// Desktop rail (collapsed icon strip) is remembered across sessions, same as
// the open/closed state — matches possmart-vue's persistent collapsed state.
const getMini = () => localStorage.getItem("drawerMini") === "true";

export default {
  components: { BranchStatusChip },
  data() {
    return {
      clipped: false,
      drawer: getDrawer(),
      miniVariant: getMini(),
      /** User's manual open/close state per collapsible group (menu code/id → bool). */
      openState: {},
      dialog: false,
      logoutDialog: false,
      branchDialog: false,
      branchLoading: false,
      branchSwitching: false,
      availableBranches: [],
      selectedBranchId: null,
      form: {
        check: false,
        title: "terms_and_conditions",
        text: "terms_and_conditions_description",
        con_txt: "agree",
        can_txt: "cancel",
        price: 0,
        try: 3,
        package: 1,
      },
      packages: [
        {
          value: 1,
          name: "free",
          price: 0,
          try: "3 Months",
          btn: "get_free",
          desc: "free_try_3_month",
          icon: "fal fa-building",
        },
        {
          value: 2,
          name: "pro",
          price: 100000,
          try: 0,
          btn: "get_pro",
          desc: "pro_description",
          icon: "fal fa-wallet",
        },
      ],
    };
  },

  watch: {
    drawer(val) {
      localStorage.setItem("drawer", val);
      this.$emit("toggle", this.expanded);
    },
    miniVariant(val) {
      localStorage.setItem("drawerMini", val ? "true" : "false");
      this.$emit("toggle", this.expanded);
    },
    // Keep the group containing the active page open (possmart-vue behavior):
    // navigating to a sub-menu page auto-expands its group, even if it was
    // collapsed — the page you landed on must be visible.
    $route() {
      this.syncRouteGroup();
    },
    // auth_menus load asynchronously — once the menu is known, open the
    // group that holds the current page.
    menuSections: {
      handler() {
        this.syncRouteGroup();
      },
      immediate: true,
    },
  },
  mixins: [myMixin],
  created() {
    // Desktop always keeps the sidebar visible (rail or full) like possmart-vue;
    // mobile opens it as a drawer overlay.
    if (this.$vuetify.breakpoint.mobile) {
      this.drawer = false;
    } else {
      this.drawer = true;
    }
  },

  mounted() {
    // Report the initial state so header arrows can point the right way
    this.$emit("toggle", this.expanded);

    document.addEventListener("keydown", this.onSidebarShortcut);
  },

  beforeDestroy() {
    document.removeEventListener("keydown", this.onSidebarShortcut);
  },

  computed: {
    ...mapGetters({
      user: "auth/user",
      isLoggedIn: "auth/isLoggedIn",
      store_type: "auth/store_type",
    }),
    rail() {
      return this.miniVariant;
    },
    isDesktop() {
      return this.$vuetify.breakpoint.mdAndUp;
    },
    /** True when the sidebar is shown at full width (arrow points left). */
    expanded() {
      return this.isDesktop ? !this.miniVariant : this.drawer;
    },
    storeName() {
      return (
        this.user?.stores?.name ||
        this.user?.stores?.store_id?.name ||
        "PosSmart"
      );
    },
    storeLogo() {
      const logo = this.user?.stores?.logo || this.user?.stores?.store_id?.logo;
      return logo ? Help.img(logo) : null;
    },
    branchName() {
      return (
        this.user?.branch_id?.name || this.user?.stores?.store_id?.name || ""
      );
    },
    canSwitchBranch() {
      return this.MyRole("change_branch");
    },
    displayName() {
      if (this.user) {
        const parts = [this.user.first_name, this.user.last_name].filter(
          Boolean,
        );
        return parts.length ? parts.join(" ") : "PosSmart";
      }
      return "PosSmart";
    },
    initials() {
      const name = this.displayName.trim();
      const parts = name.split(/\s+/);
      return (
        ((parts[0] && parts[0][0]) || "P") + ((parts[1] && parts[1][0]) || "")
      );
    },
    userAvatar() {
      return this.user?.avatar ? Help.img(this.user.avatar) : null;
    },
    menus() {
      const data = Help.addValueKey(this.user.auth_menus);
      return data;
    },
    menuSections() {
      const sections = [];
      const sectionMap = {};

      (this.menus || []).forEach((item) => {
        if (!this.shouldShowMenu(item)) {
          return;
        }

        let header = item.menu_header_id ? item.menu_header : null;
        if (header && !this.shouldShowMenuHeader(header)) {
          return;
        }
        if (!header && this.isSettingsItem(item)) {
          header = { id: "system", code: "system" };
        }

        const key = header ? `header-${header.id}` : "no-header";
        const menu = this.addStockReportMenu(
          (item.menu || []).filter((child) =>
            this.shouldShowMenu(child),
          ),
        );
        menu.sort((a, b) => this.menuPosition(a) - this.menuPosition(b));
        const visibleItem = {
          ...item,
          menu,
        };

        if (!visibleItem.to && item.menu && item.menu.length && !menu.length) {
          return;
        }

        if (!sectionMap[key]) {
          sectionMap[key] = {
            key,
            header,
            items: [],
          };
          sections.push(sectionMap[key]);
        }

        sectionMap[key].items.push(visibleItem);
      });

      sections.forEach((sec) => {
        sec.items.sort((a, b) => {
          const isSettingA = this.isSettingsItem(a);
          const isSettingB = this.isSettingsItem(b);
          if (isSettingA && !isSettingB) return 1;
          if (!isSettingA && isSettingB) return -1;
          return this.menuPosition(a) - this.menuPosition(b);
        });
      });

      return sections.sort((a, b) => {
        const isSettingSecA = a.header && this.isSettingsItem(a.header);
        const isSettingSecB = b.header && this.isSettingsItem(b.header);
        if (isSettingSecA && !isSettingSecB) return 1;
        if (!isSettingSecA && isSettingSecB) return -1;
        if (!a.header && b.header) return -1;
        if (a.header && !b.header) return 1;
        if (!a.header && !b.header) return 0;
        return this.menuPosition(a.header) - this.menuPosition(b.header);
      });
    },
  },
  methods: {
    isSettingsItem(item) {
      if (!item) return false;
      const code = String(item.code || "").toLowerCase();
      const to = String(item.to || "").toLowerCase();
      return (
        code === "setting" ||
        code === "settings" ||
        code === "system" ||
        to === "/administrator/setting" ||
        to === "/setting"
      );
    },
    isActive(item) {
      return (
        !item || item.is_active === undefined || Number(item.is_active) === 1
      );
    },
    menuPosition(item) {
      const position = Number(item?.position);
      return Number.isFinite(position) ? position : 9999;
    },
    matchesMenuType(item) {
      const menuType = item?.menu_type;
      if (!menuType || menuType === "ALL") {
        return true;
      }
      return (
        String(menuType).toUpperCase() ===
        String(this.store_type || "").toUpperCase()
      );
    },
    matchesDrawerType(item) {
      // auth_menus usually omit `type`; admin layout still needs admin menus
      if (!item?.type) {
        return true;
      }
      if (this.user && Number(this.user.role) === 1) {
        return item.type === "admin" || item.type === "user";
      }
      return item.type === "user";
    },
    shouldShowMenuHeader(header) {
      return (
        this.isActive(header) &&
        this.matchesDrawerType(header) &&
        this.matchesMenuType(header)
      );
    },
    shouldShowMenu(item) {
      if (!item) return false;
      // Merge duplicate standalone top-level invoice in admin layout; keep in Documents submenu only
      const isAdmin =
        item.type === "admin" || (this.user && Number(this.user.role) === 1);
      if (
        isAdmin &&
        !item.parent_id &&
        (item.code === "invoice" || item.to === "/administrator/invoice")
      ) {
        return false;
      }
      if (
        item.code === "report_product_page" ||
        item.to === "/report-product" ||
        item.code === "report_stock_alert" ||
        item.to === "/report-stock-alert"
      ) {
        return false;
      }
      return (
        this.isActive(item) &&
        this.matchesDrawerType(item) &&
        this.matchesMenuType(item)
      );
    },
    addStockReportMenu(items) {
      if (!Array.isArray(items)) return [];

      let result = items.filter(
        (item) =>
          item &&
          item.to !== "/report-product" &&
          item.to !== "/report-stock-alert" &&
          item.code !== "report_product_page" &&
          item.code !== "report_stock_alert"
      );

      const isReportGroup = result.some(
        (item) =>
          item &&
          (item.to === "/report-bill" || item.code === "report_bill")
      );

      if (isReportGroup) {
        let stockItem = result.find(
          (item) =>
            item &&
            (item.to === "/report-stock" || item.code === "report_stock")
        );
        result = result.filter(
          (item) =>
            item &&
            item.to !== "/report-stock" &&
            item.code !== "report_stock"
        );
        if (!stockItem) {
          stockItem = {
            code: "report_stock",
            to: "/report-stock",
            is_active: 1,
            position: 2,
            icon: "fal fa-boxes",
          };
        } else {
          stockItem = { ...stockItem, position: 2 };
        }

        const billIndex = result.findIndex(
          (item) =>
            item &&
            (item.to === "/report-bill" || item.code === "report_bill")
        );
        if (billIndex !== -1) {
          result.splice(billIndex + 1, 0, stockItem);
        } else {
          result.unshift(stockItem);
        }
        return result;
      }

      return result.map((item) =>
        item && item.menu && item.menu.length
          ? { ...item, menu: this.addStockReportMenu(item.menu) }
          : item
      );
    },
    getMenuLabel(item, locale = this.$i18n.locale) {
      if (!item) {
        return "";
      }

      if (item.code === "report_bill") {
        const translated = this.$t("report_bill");
        if (translated && translated !== "report_bill") {
          return translated;
        }
      }

      const normalizedLocale = locale === "cn" ? "ch" : locale;
      const localeAliases = {
        en: ["en", "eng", "english"],
        la: ["la", "lao"],
        th: ["th", "thai"],
        ch: ["ch", "cn", "zh", "china", "chinese"],
      };
      const aliases = localeAliases[normalizedLocale] || [normalizedLocale];
      const labelKeys = aliases.flatMap((key) => [
        `label_${key}`,
        `name_${key}`,
        `${key}_name`,
      ]);
      labelKeys.push("label", "name", "title");

      for (const key of labelKeys) {
        if (item[key]) {
          return item[key];
        }
      }

      const translated = this.$t(item.code);
      return translated !== item.code ? translated : item.code;
    },
    isUploadedIcon(item) {
      const icon = item && item.icon;
      return (
        typeof icon === "string" && /\.(svg|png|jpe?g)(\?.*)?$/i.test(icon)
      );
    },
    isLineIcon(item) {
      const icon = item && item.icon;
      return (
        typeof icon === "string" &&
        (item.icon_type === "line" || /^lni\s/i.test(icon))
      );
    },

    // ---- rail / collapse behaviour (possmart-vue toggleSidebar) ----
    toggleSidebar() {
      if (this.isDesktop) {
        // desktop: collapse to the icon rail / expand back to full width
        this.miniVariant = !this.miniVariant;
      } else {
        // mobile: open/close the drawer overlay
        this.drawer = !this.drawer;
      }
    },
    /** Rail group click: expand the sidebar with that group already open. */
    expandFromRail(item) {
      this.$set(this.openState, item.code || item.id, true);
      this.miniVariant = false;
    },
    /** First collapsible group opens by default; manual toggles and the
        active page's group take priority. */
    isGroupOpen(item) {
      const key = item.code || item.id;
      if (
        key !== undefined &&
        key !== null &&
        this.openState[key] !== undefined
      ) {
        return this.openState[key];
      }
      return item.value === true;
    },
    /** Vuetify's v-list-group reports every open/close via @input
        (Toggleable's model event). */
    onGroupChange(item, val) {
      this.$set(this.openState, item.code || item.id, val);
    },
    /** Stable key — the group's open state is reactive via `value`, so we
        never need to remount it. */
    groupKey(item) {
      return item.code || item.id || "group";
    },
    /** Open the collapsible group whose sub-menu holds the current page. */
    findRouteGroupKey() {
      const path = this.$route.path;
      for (const section of this.menuSections) {
        for (const item of section.items) {
          if (
            item.menu &&
            item.menu.length &&
            item.menu.some((child) => child.to && child.to === path)
          ) {
            return item.code || item.id;
          }
        }
      }
      return null;
    },
    syncRouteGroup() {
      const key = this.findRouteGroupKey();
      if (key) {
        this.$set(this.openState, key, true);
      }
    },
    railActive(item) {
      if (item.menu && item.menu.length) {
        return item.menu.some(
          (child) => child.to && this.$route.path === child.to,
        );
      }
      return item.to && this.$route.path === item.to;
    },

    onSidebarShortcut(event) {
      if (!(event.metaKey || event.ctrlKey)) {
        return;
      }
      if (event.key !== "b" && event.key !== "B") {
        return;
      }
      const el = event.target;
      if (!el || !(el instanceof Element)) {
        return;
      }
      const tag = el.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") {
        return;
      }
      if (el.isContentEditable) {
        return;
      }
      event.preventDefault();
      this.toggleSidebar();
    },

    logoutMethod() {
      this.logoutDialog = true;
    },

    async openBranchSwitcher() {
      if (!this.canSwitchBranch || this.branchLoading) {
        return;
      }

      this.selectedBranchId = this.user?.branch_id?.id || null;
      this.branchDialog = true;
      this.branchLoading = true;
      try {
        const { data } = await this.$http.get("branches");
        if (data.status) {
          const branches = data.data;
          this.availableBranches = Array.isArray(branches)
            ? branches
            : branches?.data || [];
        }
      } catch (error) {
        await toast(this, error.message, "error");
      } finally {
        this.branchLoading = false;
      }
    },

    async confirmBranchSwitch() {
      if (!this.selectedBranchId || this.branchSwitching) {
        return;
      }

      const target = this.availableBranches.find(
        (b) => String(b.id) === String(this.selectedBranchId),
      );
      if (target && target.is_expired) {
        return;
      }

      if (String(this.selectedBranchId) === String(this.user?.branch_id?.id)) {
        this.branchDialog = false;
        return;
      }

      this.branchSwitching = true;
      try {
        const { data } = await this.$http.post(
          `set_branch/${this.selectedBranchId}`,
        );
        if (data.status) {
          await setUser(this);
          this.branchDialog = false;
          window.location.reload();
        } else if (data.message) {
          await toast(this, data.message, "error");
        }
      } catch (error) {
        await toast(this, error.message, "error");
      } finally {
        this.branchSwitching = false;
      }
    },

    async confirmLogout() {
      try {
        await this.$store.dispatch("auth/logout", this);
        window.location.href = "/";
      } catch (error) {
        console.log(error);
      } finally {
        this.logoutDialog = false;
      }
    },

    async askForJoin() {
      try {
        const { data } = await this.$http.post("check_join");
        if (!data.data) {
          // join the to online store
          const { isConfirmed } = await confirm({
            props: this,
            title: "join_e_commerce",
            text: "join_e_commerce_description",
            con_txt: "join",
            can_txt: "later",
            color_can: "red",
          });
          if (isConfirmed) {
            this.dialog = true;
          }
        } else {
          // push to online path
          const host = window.location.origin;
          const url = `${host}/dashboard/index`;
          window.open(url, "_blank");
        }
      } catch (error) {
        console.log(error);
      }
    },
    async joinSubmit() {
      try {
        // find the package by form.package
        const _pack = this.packages.find(
          (item) => item.value === this.form.package,
        );
        const body = {
          price: _pack.price,
          type: _pack.name,
        };
        const { data } = await this.$http.post("request_join", body);
        if (data.status) {
          await toast(this, data.message, "success");
          this.dialog = false;
        }
      } catch (error) {
        console.log(error);
      }
    },
  },
};
</script>

<style scoped>
.row .col .v-card .v-card__actions {
  padding: 15px;
  position: absolute;
  bottom: 0;
  width: 100%;
  left: 0;
  right: 0;
}

/* ============================================================
   PosSmart v2 sidebar — ported 1:1 from possmart-vue's SideNav.vue
   (white shell, brand #5b39c9 active pill, slate text, 72px rail)
   ============================================================ */

.theme--dark .drawer-left {
  background: #171b21;
}

/* LineIcons (lni lni-*) rendered as a plain <i> — avoids Vuetify's v-icon
   font-family override so the LineIcons glyph shows correctly. */
.ps-sidebar-lni {
  font-size: 16px;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: inherit;
}

/* Icon slot: fixed 18px so uploaded images and FA glyphs align */
.ps-menu-icon {
  width: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* ---- Section label (SideNav: text-[10px] font-black slate-400 uppercase) ---- */
.ps-menu-section-label {
  font-size: 10px;
  font-weight: 900;
  color: #94a3b8;
  letter-spacing: 0.1em;
}

.theme--dark .ps-menu-section-label {
  color: rgba(148, 163, 184, 0.7);
}

/* ---- Store header (prepend) — SideNav store header block ----
   Total height matches the sale page app bar: 64px content + the 1px bottom
   border that main.css paints on .v-navigation-drawer__prepend
   (#e0e0e0 light / #2f333a dark) = 65px, same line as the app bar. */
.ps-store-header {
  height: 64px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 20px;
  flex-shrink: 0;
}

/* main.css gives the drawer prepend 1px top/bottom padding, which pushes the
   header line 1px below the store header — remove it so the line sits flush
   at the 65px mark, exactly like the app bar's border. */
.drawer-left ::v-deep .v-navigation-drawer__prepend {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

.theme--dark .ps-store-header {
  border-bottom-color: #2f333a;
}

.ps-store-header--rail {
  justify-content: center;
  padding: 0;
  gap: 0;
}

.ps-store-logo {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  background: #f3f0fc;
  color: #5b39c9;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}

.ps-store-logo__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ps-store-logo__icon {
  color: #5b39c9 !important;
  font-size: 18px;
}

.ps-store-meta {
  min-width: 0;
}

.ps-store-meta--switchable {
  cursor: pointer;
}

.ps-store-name {
  font-weight: 900;
  font-size: 14px;
  line-height: 1.25;
  color: #141021;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.theme--dark .ps-store-name {
  color: #e5e7eb;
}

.ps-store-branch {
  font-size: 11px;
  font-weight: 500;
  color: #94a3b8;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.theme--dark .ps-store-branch {
  color: rgba(226, 232, 240, 0.6);
}

/* ---- Menu items (SideNav nav-item classes) ---- */
.drawer-left ::v-deep .v-list-item {
  min-height: 44px;
  padding: 0 12px;
  margin: 0 12px 2px;
  border-radius: 12px;
  border: 1px solid transparent;
  transition: background-color 0.15s ease, border-color 0.15s ease,
    color 0.15s ease;
}

/* Vuetify draws the hover/focus/active overlay on .v-list-item--link:before,
   which fills the item box with NO border-radius — so hover corners looked
   square while the active pill was rounded. Inherit the item's radius so the
   hover overlay matches the active pill exactly. */
.drawer-left ::v-deep .v-list-item:before {
  border-radius: inherit;
}

.drawer-left ::v-deep .v-list-item__icon {
  margin-right: 12px !important;
}

.drawer-left ::v-deep .v-list-item .v-icon,
.drawer-left ::v-deep .v-list-item .ps-sidebar-lni {
  color: #64748b;
}

.theme--dark .drawer-left ::v-deep .v-list-item .v-icon,
.theme--dark .drawer-left ::v-deep .v-list-item .ps-sidebar-lni {
  color: rgba(226, 232, 240, 0.7);
}

.drawer-left ::v-deep .v-list-item__title {
  color: #64748b;
  font-weight: 500;
  font-size: 0.875rem;
}

.theme--dark .drawer-left ::v-deep .v-list-item__title {
  color: rgba(226, 232, 240, 0.8);
}

/* No hover background — hover is signaled by the text/icon color change
   below only. Vuetify's own default hover tint (.v-list-item:hover:before,
   opacity 0.04) is suppressed the same way. */
.drawer-left ::v-deep .v-list-item:hover:not(.v-list-item--active):before {
  opacity: 0 !important;
}

.drawer-left
  ::v-deep
  .v-list-item:hover:not(.v-list-item--active)
  .v-list-item__title {
  color: #5b39c9;
}

.drawer-left ::v-deep .v-list-item:hover:not(.v-list-item--active) .v-icon,
.drawer-left
  ::v-deep
  .v-list-item:hover:not(.v-list-item--active)
  .ps-sidebar-lni {
  color: #5b39c9;
}

.drawer-left ::v-deep .v-list-item--active .v-list-item__title {
  color: #5b39c9;
  font-weight: 700;
}

.drawer-left ::v-deep .v-list-item--active .v-icon,
.drawer-left ::v-deep .v-list-item--active .ps-sidebar-lni {
  color: #5b39c9 !important;
}

/* Active-item accent bar — sits flush against the drawer edge; the item's
   own 12px margin is what creates the gap before the active pill starts.
   Excludes submenu items: they share this same :after pseudo-element for
   the tree branch highlight instead (see "Tree branch" below) — matching
   both here would leak this rule's background/border-radius/bottom into
   that one, since equal-specificity rules only override the properties
   they actually redeclare. */
.drawer-left
  ::v-deep
  .v-list-item--active:not(.v-list-group__header):not(
    .v-list-group__items *
  ):after {
  content: "";
  position: absolute;
  left: -12px;
  top: 6px;
  bottom: 6px;
  width: 3px;
  border-radius: 0 3px 3px 0;
  background: #5b39c9;
}

/* Collapsible group header stays neutral even when a child is active —
   it just needs to look clickable, same as possmart-vue's toggle button. */
.drawer-left ::v-deep .v-list-group__header {
  min-height: 44px;
  border-radius: 12px;
  padding: 0 12px;
  margin: 0 12px 2px;
}

.drawer-left ::v-deep .v-list-group__header:hover:before {
  opacity: 0 !important;
}

.drawer-left ::v-deep .v-list-group__header .v-list-item__title {
  color: #475569;
  font-weight: 500;
}

.theme--dark .drawer-left ::v-deep .v-list-group__header .v-list-item__title {
  color: rgba(226, 232, 240, 0.85);
}

/* The group header must never look "active": Vuetify marks it
   .v-list-item--active whenever the group is open, but only the sub-menu
   item should highlight. possmart-vue keeps the collapsible header neutral
   at all times — open or collapsed — so neutralize the pill here. */
.drawer-left ::v-deep .v-list-group__header.v-list-item--active {
  background: transparent !important;
  border-color: transparent !important;
}

.drawer-left ::v-deep .v-list-group__header.v-list-item--active:before,
.drawer-left ::v-deep .v-list-group__header.v-list-item--active:hover:before {
  opacity: 0 !important;
}

.drawer-left
  ::v-deep
  .v-list-group__header.v-list-item--active
  .v-list-item__title {
  color: #475569;
  font-weight: 500;
}

.theme--dark
  .drawer-left
  ::v-deep
  .v-list-group__header.v-list-item--active
  .v-list-item__title {
  color: rgba(226, 232, 240, 0.85);
}

.drawer-left ::v-deep .v-list-group__header.v-list-item--active .v-icon,
.drawer-left
  ::v-deep
  .v-list-group__header.v-list-item--active
  .ps-sidebar-lni {
  color: #64748b !important;
}

.theme--dark
  .drawer-left
  ::v-deep
  .v-list-group__header.v-list-item--active
  .v-icon,
.theme--dark
  .drawer-left
  ::v-deep
  .v-list-group__header.v-list-item--active
  .ps-sidebar-lni {
  color: rgba(226, 232, 240, 0.7) !important;
}

/* Vuetify rotates a sub-group's PREPEND icon 180° when the group opens
   (it assumes a chevron there). Our prepend is the real menu icon, so stop
   the flip — the append chevron is the only open/close indicator. */
.drawer-left
  ::v-deep
  .v-list-group--sub-group.v-list-group--active
  .v-list-group__header__prepend-icon
  .v-icon {
  transform: none !important;
}

/* chevron spins to point up when the group opens (v-list-group--active = live state) */
.ps-group-chevron {
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.drawer-left ::v-deep .v-list-group--active .ps-group-chevron {
  transform: rotate(180deg);
}

/* ---- Submenu ---- */
.drawer-left ::v-deep .v-list-group__items {
  position: relative;
  margin: 0 0 2px;
  padding-left: 0;
}

.drawer-left ::v-deep .v-list-group__items .v-list-item {
  min-height: 44px;
  padding: 0 12px 0 52px;
  margin: 0 12px 2px;
}

/* Dot marker — replaces the old tree-line connector. A plain bullet reads
   as "nested item" on its own, so no trunk/branch lines are needed.
   min-height must be reset: Vuetify's own .v-list-item:after sets
   min-height: inherit (44px) for an unrelated flex-height trick, which
   would otherwise stretch this into a tall block instead of a small dot. */
.drawer-left ::v-deep .v-list-group__items .v-list-item:after {
  content: "";
  position: absolute;
  left: 38px;
  top: 50%;
  width: 6px;
  height: 6px;
  min-height: 0;
  border-radius: 50%;
  background: #94a3b8;
  transform: translateY(-50%);
}

.theme--dark .drawer-left ::v-deep .v-list-group__items .v-list-item:after {
  background: rgba(226, 232, 240, 0.6);
}

/* Active submenu item: same tinted pill as a top-level active item
   (Vuetify's default .v-list-item--active:before, left un-suppressed here),
   plus the dot fills purple in place of the outer edge accent bar, which
   wouldn't fit a nested/indented row. */
.drawer-left ::v-deep .v-list-group__items .v-list-item--active:after {
  background: #5b39c9;
}

/* ---- Collapsed rail (72px icon strip) ---- */
.drawer-left--rail ::v-deep .v-list-item {
  margin: 0 10px 2px;
  padding: 0 !important;
  justify-content: center;
}

.drawer-left--rail
  ::v-deep
  .v-list-item--active:not(.v-list-group__header):after {
  left: -10px;
}

/* group icons in the rail get the active pill manually (no :to to match) */
.drawer-left .ps-rail-active {
  background: #f3f0fc !important;
}

.drawer-left .ps-rail-active ::v-deep .v-icon,
.drawer-left .ps-rail-active ::v-deep .ps-sidebar-lni {
  color: #5b39c9 !important;
}

/* ---- User footer (append) — SideNav user block ---- */
.ps-user-wrap {
  border-top: 1px solid #e2e8f0;
}

.theme--dark .ps-user-wrap {
  border-top-color: #2f333a;
}

.ps-user {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
}

.ps-user--rail {
  flex-direction: column;
  gap: 8px;
  padding: 12px 0;
}

.ps-user-avatar {
  width: 40px;
  height: 40px;
  border-radius: 999px;
  background: #5b39c9;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
}

.ps-user-avatar__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.ps-user-avatar__initials {
  font-size: 14px;
  font-weight: 900;
  line-height: 1;
}

.ps-user-meta {
  min-width: 0;
  flex: 1;
}

.ps-user-name {
  font-weight: 500;
  font-size: 14px;
  line-height: 1.25;
  color: #141021;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.theme--dark .ps-user-name {
  color: #e5e7eb;
}

.ps-user-role {
  font-size: 11px;
  font-weight: 400;
  color: #94a3b8;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.theme--dark .ps-user-role {
  color: rgba(226, 232, 240, 0.6);
}

.ps-logout-btn {
  width: 32px;
  height: 32px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  border-radius: 8px;
  color: #dc2626;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: color 0.15s ease, border-color 0.15s ease,
    background-color 0.15s ease;
}

/* Soft red sign-out button (same bordered shape as drawer-cart-header__icon-btn) */
.ps-logout-btn:hover {
  color: #b91c1c !important;
  border-color: #fca5a5 !important;
  background: #fee2e2 !important;
}

.theme--dark .ps-logout-btn {
  background: transparent;
  border-color: #2f333a;
  color: #f87171;
}

.theme--dark .ps-logout-btn:hover {
  color: #fca5a5 !important;
  border-color: rgba(239, 68, 68, 0.55) !important;
  background: rgba(239, 68, 68, 0.2) !important;
}

.ps-logout-btn--rail {
  margin: 0 auto;
}
</style>

<style>
/* Vuetify tooltips render in the app overlay (outside this component), so the
   rail tooltip bubble is styled globally — dark bubble, same as possmart-vue
   SideNav's .tooltip (bg #141021, 11px bold, 8px radius, soft shadow). */
.ps-rail-tooltip {
  background: #141021 !important;
  color: #ffffff !important;
  font-size: 11px !important;
  font-weight: 700 !important;
  line-height: 1 !important;
  padding: 7px 10px !important;
  border-radius: 8px !important;
  box-shadow: 0 4px 14px rgba(20, 16, 33, 0.3) !important;
  opacity: 1 !important;
}

.branch-option--active {
  background: rgba(91, 57, 201, 0.1) !important;
  border-radius: 10px;
}

.theme--dark .branch-option--active {
  background: rgba(139, 117, 230, 0.2) !important;
}
</style>
